import React from 'react';
import { Box, Typography, Container, Grid, Alert, Chip, Skeleton } from '@mui/material';
import PanelCard from '../../design/PanelCard';
import { formatCurrencyInt } from '../../design/format';
import { pnlColors } from '../../design/theme';
import { useConfiguration, ConfigurationData } from '../../hooks/useConfiguration';

interface SettingsProps {
  traderId?: number;
}

interface FieldSpec {
  label: string;
  description: string;
  render: (config: ConfigurationData) => React.ReactNode;
}

interface GroupSpec {
  title: string;
  fields: FieldSpec[];
}

const booleanChip = (value: boolean): React.ReactNode =>
  value ? <Chip size="small" label="On" color="success" /> : <Chip size="small" label="Off" />;

const groups: GroupSpec[] = [
  {
    title: 'Trading window',
    fields: [
      {
        label: 'Start time',
        description: 'Daily trading start time',
        render: (c) => (
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {c.start_time}
          </Box>
        ),
      },
      {
        label: 'Cycle interval',
        description: 'Interval between checks',
        render: (c) => `${c.sleep_time_between_activities}s`,
      },
    ],
  },
  {
    title: 'Risk',
    fields: [
      {
        label: 'Max profit',
        description: 'Take-profit threshold',
        render: (c) => (
          <Box component="span" sx={{ color: pnlColors.gain }}>
            {`+${(c.max_profit * 100).toFixed(1)}%`}
          </Box>
        ),
      },
      {
        label: 'Max loss',
        description: 'Stop-loss threshold',
        render: (c) => (
          <Box component="span" sx={{ color: pnlColors.loss }}>
            {`−${(Math.abs(c.max_loss) * 100).toFixed(1)}%`}
          </Box>
        ),
      },
      {
        label: 'Max days to hold',
        description: 'Maximum holding period',
        render: (c) => `${c.max_days_to_hold} days`,
      },
    ],
  },
  {
    title: 'Capital',
    fields: [
      {
        label: 'Starting investment',
        description: 'Total capital allocated to the trader',
        render: (c) => formatCurrencyInt(c.starting_investment),
      },
      {
        label: 'Per-stock investment',
        description: 'Capital allocated per position',
        render: (c) => formatCurrencyInt(c.single_stock_investment),
      },
    ],
  },
  {
    title: 'Filters',
    fields: [
      {
        label: 'SPY regime filter',
        description: 'Skip buys when SPY is below its 200-day MA',
        render: (c) => booleanChip(c.regime_filter_spy_enabled),
      },
      {
        label: 'QQQ regime filter',
        description: 'Skip buys when QQQ is below its 200-day MA',
        render: (c) => booleanChip(c.regime_filter_qqq_enabled),
      },
      {
        label: 'Min market cap',
        description: 'Skip buys below this market cap',
        render: (c) => (c.min_market_cap > 0 ? formatCurrencyInt(c.min_market_cap) : 'Off'),
      },
    ],
  },
  {
    title: 'Misc',
    fields: [
      {
        label: 'Trader name',
        description: 'Trader identifier',
        render: (c) => c.trader_name,
      },
      {
        label: 'Strategy',
        description: 'Active trading strategy',
        render: (c) => `Strategy ${c.strategy_number}`,
      },
      {
        label: 'Telegram tracking',
        description: 'Trade notifications via Telegram',
        render: (c) => booleanChip(c.track_on_telegram),
      },
    ],
  },
];

const DefinitionRow: React.FC<{ label: string; description: string; value: React.ReactNode }> = ({
  label,
  description,
  value,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      py: 1,
      '& + &': { borderTop: '1px solid rgba(15, 23, 42, 0.07)' },
    }}
  >
    <Box sx={{ pr: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    </Box>
    <Typography
      variant="body2"
      component="div"
      sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}
    >
      {value}
    </Typography>
  </Box>
);

const Settings: React.FC<SettingsProps> = ({ traderId }) => {
  const { configuration, loading, error } = useConfiguration(traderId);

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Trader configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Read-only — set via environment variables on the trader container
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {loading && !configuration
          ? Array.from({ length: 5 }, (_, i) => (
              <Grid item xs={12} md={6} lg={4} key={i}>
                <PanelCard title="">
                  {Array.from({ length: 3 }, (_, j) => (
                    <Skeleton key={j} variant="rounded" height={44} sx={{ my: 1 }} />
                  ))}
                </PanelCard>
              </Grid>
            ))
          : configuration &&
            groups.map((group) => (
              <Grid item xs={12} md={6} lg={4} key={group.title}>
                <PanelCard title={group.title}>
                  {group.fields.map((field) => (
                    <DefinitionRow
                      key={field.label}
                      label={field.label}
                      description={field.description}
                      value={field.render(configuration)}
                    />
                  ))}
                </PanelCard>
              </Grid>
            ))}
      </Grid>
    </Container>
  );
};

export default Settings;
